/**
 * SM-3232: Shared client-side validation for supplier form attachment questions.
 *
 * The attachment question renders the note "Upload files in PDF, Word, and Excel
 * formats, up to 5 MB." This module centralizes the enforced rule; keep the
 * UI note in sync with these constants wherever it is rendered.
 */

/** Maximum allowed attachment size, in megabytes. Keep in sync with the UI note. */
export const MAX_ATTACHMENT_SIZE_MB = 5;

/** Maximum allowed attachment size, in bytes. */
export const MAX_ATTACHMENT_SIZE_BYTES = MAX_ATTACHMENT_SIZE_MB * 1024 * 1024;

/** File extensions permitted per the "PDF, Word, and Excel" note. */
export const ALLOWED_ATTACHMENT_EXTENSIONS = [
  'pdf',
  'doc',
  'docx',
  'xls',
  'xlsx',
];

export type AttachmentValidationResult =
  | {
    valid: true;
  }
  | {
    valid: false;
    title: string;
    message: string;
  };

/**
 * Validates a selected attachment file against the size and type rules.
 * Extension-based type check (browser `file.type` is unreliable / often empty).
 */
export function validateAttachment(file: File): AttachmentValidationResult {
  const lastDot = file.name.lastIndexOf('.');
  const ext = lastDot > 0 ? file.name.slice(lastDot + 1).toLowerCase() : '';

  if (!ALLOWED_ATTACHMENT_EXTENSIONS.includes(ext)) {
    return {
      valid: false,
      title: 'Invalid File Type',
      message:
        'Only PDF, Word, and Excel files are allowed. Please select a supported file.',
    };
  }

  if (file.size > MAX_ATTACHMENT_SIZE_BYTES) {
    const sizeInMb = (file.size / (1024 * 1024)).toFixed(2);
    return {
      valid: false,
      title: 'File Too Large',
      message: `The selected file is ${sizeInMb} MB. Maximum allowed size is ${MAX_ATTACHMENT_SIZE_MB} MB.`,
    };
  }

  return { valid: true };
}
